import type { TFunction } from "i18next";
import {
  type DocumentCompliance,
  ValidationRuleSeverity,
} from "src/api/interfaces/document_validation";
import type {
  Field,
  ValidationResult,
} from "src/api/interfaces/validation_result";
import { getSopInstructions } from "src/components/Pages/DocumentInbound/Tabs/Validations/ValidationsModals/Mismatch/mismatch-table-fields-extractor";
import {
  type UserAction,
  ValidationUserActionType,
} from "src/components/UserActions/types";

export interface ComplianceHelperField {
  comment: string;
  severity: string;
  sopDetails: string;
  fields: Array<Field>;
  validationGroupId: string;
}

export const normalizeSopDetails = (text: string): string => {
  if (!text) return "";
  return text
    .replace(/[^a-zA-Z\s]/g, "")
    .replace(/\s+/g, "")
    .trim()
    .toLowerCase();
};

const getSeverityOrder = (severity: string): number => {
  switch (severity) {
    case ValidationRuleSeverity.CRITICAL.valueOf():
      return 1;
    case ValidationRuleSeverity.SEVERE.valueOf():
      return 2;
    case ValidationRuleSeverity.ERROR.valueOf():
      return 3;
    case ValidationRuleSeverity.WARNING.valueOf():
      return 4;
    default:
      return 5;
  }
};

const getUserActionOrder = (
  validationGroupId: string,
  userActionsByValidationGroup: { [key: string]: Array<UserAction> }
): number => {
  const userActions = userActionsByValidationGroup?.[validationGroupId] || [];
  const isNotViewOnly =
    userActions.length > 0 &&
    userActions.some(
      (action: any) =>
        action.type === ValidationUserActionType.OVERRIDE_METADATA
    );
  return isNotViewOnly ? 1 : 2;
};

export const createNormalizedKey = (
  sopDetails: string,
  comment: string
): string => {
  return sopDetails
    ? normalizeSopDetails(sopDetails + comment)
    : normalizeSopDetails(comment || "");
};

const createComplianceHelperField = (
  result: ValidationResult,
  sopDetails: string,
  validationGroupId: string
): ComplianceHelperField => ({
  severity: result.severity || "",
  comment: result.comment || "",
  sopDetails,
  fields: [],
  validationGroupId,
});

const addFieldsToMap = (
  globalSopMap: Record<string, ComplianceHelperField>,
  normalizedKey: string,
  fields: Field[]
): void => {
  fields?.forEach((field) => {
    const isDuplicate = globalSopMap[normalizedKey].fields.some(
      (existingField) => existingField.itemIdentifier === field.itemIdentifier
    );
    if (!isDuplicate) {
      globalSopMap[normalizedKey].fields.push(field);
    }
  });
};

const processValidationResult = (
  result: ValidationResult,
  validationGroupId: string,
  globalSopMap: Record<string, ComplianceHelperField>,
  userActionsByValidationGroup: { [key: string]: UserAction[] },
  t: TFunction
): void => {
  const sopDetails = getSopInstructions(result.comment, t);
  const baseKey = createNormalizedKey(sopDetails, t(result.comment) || "");
  const newEntryHasUserActions =
    userActionsByValidationGroup?.[validationGroupId]?.length > 0;

  if (globalSopMap[baseKey]) {
    const existingEntry = globalSopMap[baseKey];
    const existingEntryHasUserActions =
      userActionsByValidationGroup?.[existingEntry.validationGroupId]?.length >
      0;

    if (!existingEntryHasUserActions && newEntryHasUserActions) {
      globalSopMap[baseKey] = createComplianceHelperField(
        result,
        sopDetails,
        validationGroupId
      );
    }
  } else {
    globalSopMap[baseKey] = createComplianceHelperField(
      result,
      sopDetails,
      validationGroupId
    );
  }
  addFieldsToMap(globalSopMap, baseKey, result.fields || []);
};

const sortResults = (
  results: ComplianceHelperField[],
  documentCompliance: DocumentCompliance
): ComplianceHelperField[] => {
  return results
    .sort((a, b) => getSeverityOrder(a.severity) - getSeverityOrder(b.severity))
    .sort(
      (a, b) =>
        getUserActionOrder(
          a.validationGroupId,
          documentCompliance.userActionsByValidationGroup
        ) -
        getUserActionOrder(
          b.validationGroupId,
          documentCompliance.userActionsByValidationGroup
        )
    );
};

export const extractAllValidationResults = (
  validationResultsByGroup: Record<string, any>,
  documentCompliance: DocumentCompliance,
  t: TFunction
): Record<string, ComplianceHelperField[]> => {
  const globalSopMap: Record<string, ComplianceHelperField> = {};

  Object.keys(validationResultsByGroup).forEach((validationGroupId) => {
    const groupValidationResults =
      validationResultsByGroup[validationGroupId]?.validationResults || [];

    groupValidationResults.forEach((result: ValidationResult) => {
      processValidationResult(
        result,
        validationGroupId,
        globalSopMap,
        documentCompliance.userActionsByValidationGroup,
        t
      );
    });
  });

  const unifiedResults = Object.values(globalSopMap);
  const sortedResults = sortResults(unifiedResults, documentCompliance);

  return { sorted: sortedResults };
};

export const getLocationText = (fields: Field[], t: TFunction): string => {
  if (!fields || fields.length === 0) return "";
  const itemValues = fields
    .filter((field) => field.itemValue)
    .map((field) => t(field.itemValue.replace(/"/g, "")));
  const result =
    itemValues.length > 1
      ? itemValues.slice(0, -1).join(", ") +
        " " +
        t("aic_doc_page_inbound_document_compliance_helper_and") +
        " " +
        itemValues[itemValues.length - 1]
      : itemValues.join(", ");
  return /\d/.test(result)
    ? t("aic_doc_page_inbound_document_compliance_helper_line") + " " + result
    : result;
};

export const getCriticalErrorsCount = (
  items: ComplianceHelperField[] | Record<string, ComplianceHelperField[]>
): number => {
  const flatItems = Array.isArray(items) ? items : Object.values(items).flat();

  return flatItems.filter(
    (item) =>
      item.severity === ValidationRuleSeverity.CRITICAL.valueOf() ||
      item.severity === ValidationRuleSeverity.ERROR.valueOf() ||
      item.severity === ValidationRuleSeverity.SEVERE.valueOf()
  ).length;
};
